import React, { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, ArrowLeft } from "lucide-react";

const VIETNAMESE_MONTHS = [
  "Tháng 1", "Tháng 2", "Tháng 3", "Tháng 4",
  "Tháng 5", "Tháng 6", "Tháng 7", "Tháng 8",
  "Tháng 9", "Tháng 10", "Tháng 11", "Tháng 12"
];

const VIETNAMESE_DAYS = ["T2", "T3", "T4", "T5", "T6", "T7", "CN"];

export default function VietnameseDatePicker({
  value, // "YYYY-MM-DD"
  onChange, // (isoDateString, displayString) => void
  onBlur,
  placeholder = "dd/MM/yyyy",
  minDate, // "YYYY-MM-DD"
  maxDate, // "YYYY-MM-DD"
  error,
  disabled = false,
  className = "",
  inputClassName = "",
  borderless = false,
  align = "auto", // "left" | "right" | "auto"
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [viewMode, setViewMode] = useState("date"); // "date" | "month" | "year"
  const [popoverAlign, setPopoverAlign] = useState(align === "right" ? "right" : "left");
  const containerRef = useRef(null);

  useEffect(() => {
    if (isOpen && containerRef.current) {
      if (align === "right") {
        setPopoverAlign("right");
      } else if (align === "left") {
        setPopoverAlign("left");
      } else {
        const rect = containerRef.current.getBoundingClientRect();
        if (window.innerWidth - rect.left < 310) {
          setPopoverAlign("right");
        } else {
          setPopoverAlign("left");
        }
      }
    }
  }, [isOpen, align]);

  const effectiveMinDate = minDate || "1945-01-01";
  const effectiveMaxDate = maxDate || "2099-12-31";

  const minYear = parseInt(effectiveMinDate.split("-")[0], 10) || 1945;
  const minMonth = (parseInt(effectiveMinDate.split("-")[1], 10) || 1) - 1;
  const maxYear = parseInt(effectiveMaxDate.split("-")[0], 10) || 2099;
  const maxMonth = (parseInt(effectiveMaxDate.split("-")[1], 10) || 12) - 1;

  // Format ISO to DD/MM/YYYY
  const toDisplay = (iso) => {
    if (!iso) return "";
    const parts = iso.split("-");
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return "";
  };

  const [textInput, setTextInput] = useState(toDisplay(value));

  // Sync textInput when value prop changes
  useEffect(() => {
    setTextInput(toDisplay(value));
  }, [value]);

  // Current viewing month and year in calendar popover
  const initialDate = value ? new Date(value) : new Date();
  const [viewYear, setViewYear] = useState(
    isNaN(initialDate.getTime()) ? new Date().getFullYear() : Math.min(Math.max(initialDate.getFullYear(), minYear), maxYear)
  );
  const [viewMonth, setViewMonth] = useState(
    isNaN(initialDate.getTime()) ? new Date().getMonth() : initialDate.getMonth()
  );

  // Year decade page
  const [yearPageStart, setYearPageStart] = useState(
    Math.floor((isNaN(initialDate.getTime()) ? new Date().getFullYear() : Math.min(Math.max(initialDate.getFullYear(), minYear), maxYear)) / 12) * 12
  );

  // When opening calendar, ensure view aligns with selected value or today
  useEffect(() => {
    if (isOpen) {
      setViewMode("date");
      if (value) {
        const d = new Date(value);
        if (!isNaN(d.getTime())) {
          const clampedYear = Math.min(Math.max(d.getFullYear(), minYear), maxYear);
          setViewYear(clampedYear);
          setViewMonth(d.getMonth());
          setYearPageStart(Math.floor(clampedYear / 12) * 12);
        }
      } else {
        const today = new Date();
        const clampedYear = Math.min(Math.max(today.getFullYear(), minYear), maxYear);
        setViewYear(clampedYear);
        setViewMonth(today.getMonth());
        setYearPageStart(Math.floor(clampedYear / 12) * 12);
      }
    }
  }, [isOpen, value, minYear, maxYear]);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setViewMode("date");
        if (onBlur) onBlur();
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onBlur]);

  const canPrevMonth = !(viewYear <= minYear && viewMonth <= minMonth);
  const canNextMonth = !(viewYear >= maxYear && viewMonth >= maxMonth);

  // Navigation handlers for date view
  const handlePrevMonth = (e) => {
    e.stopPropagation();
    if (!canPrevMonth) return;
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => Math.max(minYear, y - 1));
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const handleNextMonth = (e) => {
    e.stopPropagation();
    if (!canNextMonth) return;
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => Math.min(maxYear, y + 1));
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  // Select a day
  const handleSelectDay = (year, month, day) => {
    const mStr = String(month + 1).padStart(2, "0");
    const dStr = String(day).padStart(2, "0");
    const iso = `${year}-${mStr}-${dStr}`;
    const display = `${dStr}/${mStr}/${year}`;
    setTextInput(display);
    onChange(iso, display);
    setIsOpen(false);
    setViewMode("date");
  };

  // Quick Today
  const handleSelectToday = () => {
    const today = new Date();
    handleSelectDay(today.getFullYear(), today.getMonth(), today.getDate());
  };

  // Quick Clear
  const handleClear = () => {
    setTextInput("");
    onChange("", "");
    setIsOpen(false);
    setViewMode("date");
  };

  // Handle typing in input
  const handleInputChange = (e) => {
    const raw = e.target.value.replace(/[^0-9/]/g, "");
    setTextInput(raw);

    // Auto-parse if valid DD/MM/YYYY
    if (raw.length === 10) {
      const parts = raw.split("/");
      if (parts.length === 3) {
        const d = parseInt(parts[0], 10);
        const m = parseInt(parts[1], 10);
        const y = parseInt(parts[2], 10);
        if (d >= 1 && d <= 31 && m >= 1 && m <= 12 && y >= minYear && y <= maxYear) {
          const iso = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          if (iso >= effectiveMinDate && iso <= effectiveMaxDate) {
            onChange(iso, raw);
            setViewYear(y);
            setViewMonth(m - 1);
            return;
          }
        }
      }
    } else if (raw === "") {
      onChange("", "");
    }
  };

  // Days calculation for current month grid (Mon to Sun: T2=0, T3=1 ... CN=6)
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDayOfWeek = (new Date(viewYear, viewMonth, 1).getDay() + 6) % 7; // Monday = 0, Sunday = 6
  const prevMonthDays = new Date(viewYear, viewMonth, 0).getDate();

  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      {/* Input container */}
      <div className="relative w-full flex items-center">
        <input
          type="text"
          disabled={disabled}
          value={textInput}
          onChange={handleInputChange}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          placeholder={placeholder}
          className={`w-full text-xs md:text-sm font-semibold cursor-pointer transition-colors focus:outline-none ${
            borderless
              ? "border-0 bg-transparent shadow-none p-0 pr-6 text-gray-800 focus:ring-0"
              : `h-[38px] pl-3 pr-8 py-2 border rounded-lg ${
                  error
                    ? "border-red-500 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                }`
          } ${disabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""} ${inputClassName}`}
        />
        <button
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          className={`absolute inset-y-0 right-0 flex items-center pr-1 transition-colors ${
            disabled ? "text-gray-300 cursor-not-allowed" : "text-gray-400 hover:text-blue-600 cursor-pointer"
          }`}
          title="Chọn ngày"
        >
          <CalendarIcon className="w-4 h-4" />
        </button>
      </div>

      {/* Calendar Popover */}
      {isOpen && (
        <div className={`absolute z-50 mt-1 ${popoverAlign === "right" ? "right-0" : "left-0"} bg-white rounded-xl shadow-xl border border-gray-200 p-3.5 w-72 select-none animate-in fade-in zoom-in-95 duration-150`}>
          
          {/* VIEW MODE: DATE */}
          {viewMode === "date" && (
            <>
              {/* Header with Month/Year Navigation */}
              <div className="flex items-center justify-between gap-1 mb-3">
                <button
                  type="button"
                  disabled={!canPrevMonth}
                  onClick={handlePrevMonth}
                  className={`p-1.5 rounded-lg transition-colors ${!canPrevMonth ? "text-gray-300 opacity-40 cursor-not-allowed" : "hover:bg-gray-100 text-gray-600 cursor-pointer"}`}
                  title="Tháng trước"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                <div className="flex items-center gap-1.5">
                  {/* Month button */}
                  <button
                    type="button"
                    onClick={() => setViewMode("month")}
                    className="px-2.5 py-1 text-xs font-bold text-gray-800 bg-gray-100 hover:bg-blue-50 hover:text-blue-600 border border-gray-200 rounded-lg transition-colors cursor-pointer"
                    title="Nhấn để chọn tháng"
                  >
                    Tháng {viewMonth + 1}
                  </button>

                  {/* Year button */}
                  <button
                    type="button"
                    onClick={() => {
                      setYearPageStart(Math.floor(viewYear / 12) * 12);
                      setViewMode("year");
                    }}
                    className="px-2.5 py-1 text-xs font-bold text-gray-800 bg-gray-100 hover:bg-blue-50 hover:text-blue-600 border border-gray-200 rounded-lg transition-colors cursor-pointer"
                    title="Nhấn để chọn năm"
                  >
                    {viewYear}
                  </button>
                </div>

                <button
                  type="button"
                  disabled={!canNextMonth}
                  onClick={handleNextMonth}
                  className={`p-1.5 rounded-lg transition-colors ${!canNextMonth ? "text-gray-300 opacity-40 cursor-not-allowed" : "hover:bg-gray-100 text-gray-600 cursor-pointer"}`}
                  title="Tháng sau"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              {/* Days of Week Header */}
              <div className="grid grid-cols-7 gap-1 text-center mb-1">
                {VIETNAMESE_DAYS.map((d, i) => (
                  <div
                    key={d}
                    className={`text-[11px] font-bold py-1 ${
                      i === 6 ? "text-red-500" : "text-gray-500"
                    }`}
                  >
                    {d}
                  </div>
                ))}
              </div>

              {/* Calendar Day Grid */}
              <div className="grid grid-cols-7 gap-1 text-center">
                {/* Previous month padding days */}
                {Array.from({ length: firstDayOfWeek }).map((_, idx) => {
                  const dayNum = prevMonthDays - firstDayOfWeek + idx + 1;
                  return (
                    <div
                      key={`prev-${idx}`}
                      className="text-xs py-1.5 text-gray-300 pointer-events-none"
                    >
                      {dayNum}
                    </div>
                  );
                })}

                {/* Current month days */}
                {Array.from({ length: daysInMonth }).map((_, idx) => {
                  const dayNum = idx + 1;
                  const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
                  const isSelected = value === dateStr;
                  const isToday = todayStr === dateStr;
                  const isSunday = (firstDayOfWeek + idx) % 7 === 6;

                  // Check min/max date disabled
                  let isDisabled = false;
                  if (minDate && dateStr < minDate) isDisabled = true;
                  if (maxDate && dateStr > maxDate) isDisabled = true;

                  return (
                    <button
                      key={`cur-${dayNum}`}
                      type="button"
                      disabled={isDisabled}
                      onClick={() => handleSelectDay(viewYear, viewMonth, dayNum)}
                      className={`text-xs py-1.5 rounded-lg transition-all font-medium ${
                        isDisabled
                          ? "text-gray-300 cursor-not-allowed opacity-40"
                          : isSelected
                          ? "bg-blue-600 text-white font-bold shadow-xs hover:bg-blue-700"
                          : isToday
                          ? "bg-blue-50 text-blue-700 font-bold border border-blue-300 hover:bg-blue-100"
                          : isSunday
                          ? "text-red-600 hover:bg-red-50"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {dayNum}
                    </button>
                  );
                })}
              </div>

              {/* Footer Quick Actions */}
              <div className="flex items-center justify-between border-t border-gray-100 pt-2.5 mt-2.5 text-xs">
                <button
                  type="button"
                  onClick={handleClear}
                  className="text-gray-500 hover:text-red-600 font-medium px-2 py-1 rounded-md hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  Xóa
                </button>
                <button
                  type="button"
                  onClick={handleSelectToday}
                  className="text-blue-600 hover:text-blue-700 font-semibold px-2.5 py-1 rounded-md hover:bg-blue-50 transition-colors cursor-pointer"
                >
                  Hôm nay
                </button>
              </div>
            </>
          )}

          {/* VIEW MODE: MONTH SELECTION */}
          {viewMode === "month" && (
            <div>
              <div className="flex items-center justify-between gap-1 mb-3">
                <button
                  type="button"
                  onClick={() => setViewMode("date")}
                  className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700 px-2 py-1 rounded-lg hover:bg-blue-50 transition-colors cursor-pointer"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Lịch
                </button>
                <span className="text-xs font-bold text-gray-800">
                  Chọn tháng năm {viewYear}
                </span>
                <span className="w-10" />
              </div>

              <div className="grid grid-cols-3 gap-2">
                {VIETNAMESE_MONTHS.map((mName, idx) => {
                  const isCurrent = idx === viewMonth;
                  const isMonthDisabled =
                    (viewYear === minYear && idx < minMonth) ||
                    (viewYear === maxYear && idx > maxMonth) ||
                    viewYear < minYear ||
                    viewYear > maxYear;
                  return (
                    <button
                      key={idx}
                      type="button"
                      disabled={isMonthDisabled}
                      onClick={() => {
                        setViewMonth(idx);
                        setViewMode("date");
                      }}
                      className={`text-xs py-2.5 rounded-lg font-medium transition-all ${
                        isMonthDisabled
                          ? "opacity-30 text-gray-400 bg-gray-50 cursor-not-allowed border border-gray-100"
                          : isCurrent
                          ? "bg-blue-600 text-white font-bold shadow-xs cursor-pointer"
                          : "bg-gray-50 text-gray-700 hover:bg-blue-50 hover:text-blue-600 border border-gray-200 cursor-pointer"
                      }`}
                    >
                      {mName}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* VIEW MODE: YEAR SELECTION */}
          {viewMode === "year" && (
            <div>
              <div className="flex items-center justify-between gap-1 mb-3">
                <button
                  type="button"
                  disabled={yearPageStart <= minYear}
                  onClick={() => setYearPageStart((y) => Math.max(minYear, y - 12))}
                  className={`p-1.5 rounded-lg transition-colors ${yearPageStart <= minYear ? "text-gray-300 opacity-40 cursor-not-allowed" : "hover:bg-gray-100 text-gray-600 cursor-pointer"}`}
                  title="12 năm trước"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                <div className="text-xs font-bold text-gray-800">
                  {Math.max(minYear, yearPageStart)} - {Math.min(maxYear, yearPageStart + 11)}
                </div>

                <button
                  type="button"
                  disabled={yearPageStart + 11 >= maxYear}
                  onClick={() => setYearPageStart((y) => Math.min(maxYear - 11, y + 12))}
                  className={`p-1.5 rounded-lg transition-colors ${yearPageStart + 11 >= maxYear ? "text-gray-300 opacity-40 cursor-not-allowed" : "hover:bg-gray-100 text-gray-600 cursor-pointer"}`}
                  title="12 năm sau"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-3 gap-2">
                {Array.from({ length: 12 }).map((_, idx) => {
                  const y = yearPageStart + idx;
                  const isCurrent = y === viewYear;
                  const isYearDisabled = y < minYear || y > maxYear;
                  return (
                    <button
                      key={y}
                      type="button"
                      disabled={isYearDisabled}
                      onClick={() => {
                        setViewYear(y);
                        setViewMode("date");
                      }}
                      className={`text-xs py-2.5 rounded-lg font-medium transition-all ${
                        isYearDisabled
                          ? "opacity-30 text-gray-400 bg-gray-50 cursor-not-allowed border border-gray-100"
                          : isCurrent
                          ? "bg-blue-600 text-white font-bold shadow-xs cursor-pointer"
                          : "bg-gray-50 text-gray-700 hover:bg-blue-50 hover:text-blue-600 border border-gray-200 cursor-pointer"
                      }`}
                    >
                      {y}
                    </button>
                  );
                })}
              </div>

              <div className="mt-2.5 text-center">
                <button
                  type="button"
                  onClick={() => setViewMode("date")}
                  className="text-xs font-semibold text-gray-500 hover:text-blue-600 hover:underline cursor-pointer"
                >
                  ← Quay lại xem ngày
                </button>
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  );
}
